/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable no-return-assign */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@headlessui/react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { setMessage } from '../../redux/actions/message';
import { postData, depositTransactions, pensionTransactions, allTransactions, globalAccountsView } from '../../redux/reducers/data';
import getCurrentBreakpoint from '../../misc/getCurrentBreakpoint';
import Button from '../../elements/Button';
import buildBlueTransactionOptions from './buildBlueTransactionMenu';
import Sublist from './Sublist';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function isMobileLayout(breakpoint) {
  return breakpoint === 'xs' || breakpoint === 'sm' || !breakpoint;
}

function parseIf(str) {
  return typeof str === 'string' ? JSON.parse(str) : str;
}

/**
 * Displays a two-tier menu with labels that the user can apply to transaction. For some types of labels, a second level of detail is required.
 *
 * @param {array of string} transactionIds
 * @param {string} category
 *
 * @returns React.Component
 *
 * This component is passed from TableBody via browser event to the Dialog component (setDialog event) and displayed there. It receives the props passed from TableBody.
 *
 * Logging: this opens in the Dialog window context, use custom event logDialogForm to have logs displayed via DialogFormModalReceiver
 */
export default function BlueTransactionsDialog({ transactionIds, category }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'deposits' });
  const { t: tB } = useTranslation('app', { keyPrefix: 'general' });

  // Table view does not have all fields (e.g. linkedTransactions is missing), therefore get the transaction from state
  const selectTransactions = category === 'deposits' ? useSelector(depositTransactions) : useSelector(pensionTransactions);
  const transactions = selectTransactions.filter((txn) => transactionIds.includes(txn.id));
  const firstTransaction = transactions[0] || null;
  const accountId = firstTransaction?.accountId || null;
  const selectAllTransactions = useSelector(allTransactions);
  const selectAccounts = useSelector(globalAccountsView);
  const firstTransactionTags = parseIf(firstTransaction?.tags) || {};

  // show me those accounts which have connectedDepositAccounts whose id is the same as the current account
  const accountsConnectedToThisOne = selectAccounts.filter((acc) => acc.connectedDepositAccounts && accountId && acc.connectedDepositAccounts.includes(accountId));
  const loanAccountsConnectedToThisOne = accountsConnectedToThisOne.filter((acc) => acc.category === 'loans');
  const repaymentLoanAccount = firstTransactionTags.accountId ? selectAccounts.find((acc) => acc.id === firstTransactionTags.accountId && acc.category === 'loans') : null;
  const loanAccountsForRepaymentTagging = repaymentLoanAccount && !loanAccountsConnectedToThisOne.some((acc) => acc.id === repaymentLoanAccount.id)
    ? loanAccountsConnectedToThisOne.concat(repaymentLoanAccount)
    : loanAccountsConnectedToThisOne;

  const options = useMemo(
    () => buildBlueTransactionOptions({
      category,
      t,
      accountsConnectedToThisOne,
      transactions,
      loanAccountsForRepaymentTagging,
    }),
    [accountsConnectedToThisOne, category, t, transactions, loanAccountsForRepaymentTagging],
  );

  const labelsAreTheSame = useMemo(() => Boolean(firstTransaction) && transactions.every((tx) => tx.label === firstTransaction.label), [firstTransaction, transactions]);

  const nullOption = useMemo(() => options.find((option) => option.id === null) || null, [options]);

  const [selectedOption, setSelectedOption] = useState(nullOption);
  // const [selectedOption, setSelectedOption] = useState(() => {
  //   if (!labelsAreAligned) return nullOption;
  //   return options.find((option) => option.id === firstTransaction?.label) || null;
  // });

  useEffect(() => {
    // Keep the current selection once the user has made one.
    // Only auto-hydrate when current selection is missing or no longer available.
    const selectedStillAvailable = options.some((option) => option.id === selectedOption?.id);
    if (selectedStillAvailable) return;

    if (!labelsAreTheSame) {
      setSelectedOption(nullOption || null);
      return;
    }

    const matchedOption = options.find((option) => option.id === firstTransaction?.label) || nullOption || null;
    setSelectedOption(matchedOption);
  }, [labelsAreTheSame, options, firstTransaction?.label, selectedOption?.id, nullOption]);

  // TODO this is always taken from the first transaction
  const linkedAttribute = useMemo(() => {
    const tags = parseIf(firstTransaction?.tags) || {};
    return {
      linkedTransaction:
        firstTransaction?.linkedTransactions?.find((lt) => lt.tags.linkType === 'fee-to-transaction' || lt.tags.linkType === 'sale-to-transaction' || lt.tags.linkType === 'transfer') || null,
      accountId: tags.accountId || null,
      assetId: tags.assetId || null,
    };
  }, [firstTransaction]);

  const [currentBreakpoint, setCurrentBreakpoint] = useState('md'); // stores current breakpoint
  const [panesToShow, setPanesToShow] = useState([true, true]); // [menu, submenu]

  const [loading, setLoading] = useState(false);

  function handleCancel(closeDialog = false) {
    if (!closeDialog) setPanesToShow([true, false]);
    // otherwise just close the dialog
    else window.dispatchEvent(new CustomEvent('setDialog', {}));
  }

  // INITIALISE COMPONENT

  useEffect(() => {
    // Handle window resize and update breakpoint state
    const handleResize = () => {
      const newBreakpoint = getCurrentBreakpoint();
      setCurrentBreakpoint(newBreakpoint);
      return newBreakpoint;
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Set initial breakpoint
    const newBreakpoint = handleResize();

    // set panes depending on breakpoint
    if (isMobileLayout(newBreakpoint)) setPanesToShow([true, false]); // else use default
    if (!isMobileLayout(newBreakpoint) && selectedOption && !selectedOption.nextLevel) setPanesToShow([true, false]);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // HANDLE MENU OPTION CHANGE

  function handleMenuOptionChange(option) {
    setSelectedOption(option);

    // set panes depending on whether the selected option has a next level or not (and whether we are on mobile)
    if (option && option.nextLevel) {
      if (isMobileLayout(currentBreakpoint)) setPanesToShow([false, true]);
      else setPanesToShow([true, true]);
    } else if (option && !option.nextLevel) {
      setPanesToShow([true, false]);
    }
  }

  // SAVE DATA WITH LABEL ONLY

  const dispatch = useDispatch();
  async function handleSaveLabelOnly() {
    // for saving items which do not require any additional attributes (i.e. options where nextLevel = false)
    // all other ones are handled in the List component above (ca. row 220)
    if (!selectedOption || selectedOption.nextLevel) return;
    if (!selectedOption.nextLevel) {
      try {
        // if nothing has changed, do not save transactions but simply close dialog
        // this is the path for no next level options only, so it is enough to check if the label is the same before and after in all transactions
        if (transactions.every((tx) => tx.label === selectedOption.id)) {
          handleCancel(true);
          return;
        }

        setLoading(true);
        const promises = [
          dispatch(
            postData({
              data: transactions.map((transaction) => ({
                ...transaction,
                label: selectedOption.id,
                tags: {
                  ...parseIf(transaction.tags),
                  type: null,
                  accountId: null,
                  assetId: null,
                },
                linkedTransactions: transaction.linkedTransactions.filter(
                  (lt) => lt.tags.linkType !== 'fee-to-transaction' && lt.tags.linkType !== 'sale-to-transaction' && lt.tags.linkType !== 'transfer',
                ),
                importFlag: 'put',
              })),
              category,
              accountId,
            }),
          ),
        ];

        // if previously any of these transactions was a transfer, we need to remove the linked transaction and label from the other side as well
        transactions.forEach((transaction) => {
          const linkedTransferTransactionObject = transaction.linkedTransactions.find((lt) => lt.tags.linkType === 'transfer'); // { id, category, tags }
          // if transaction found (handle fails graciously)
          if (linkedTransferTransactionObject) {
            const otherTransaction = selectAllTransactions.find((tx) => tx.id === linkedTransferTransactionObject?.id);

            promises.push(
              dispatch(
                postData({
                  data: [
                    {
                      ...otherTransaction,
                      label: null, // sets label to null
                      linkedTransactions: otherTransaction.linkedTransactions.filter((lt) => lt.tags.linkType !== 'transfer'), // removes the transfer object
                      importFlag: 'put',
                    },
                  ],
                  category: otherTransaction.category,
                  accountId: otherTransaction.accountId,
                }),
              ),
            );
          }
        });

        await Promise.all(promises);
        setLoading(false);
        handleCancel(true);
      } catch (e) {
        setLoading(false);
        dispatch(setMessage('dataUpdateError'));
      }
    }
  }

  return (
    <div className="mx-auto max-w-3xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
      <Combobox value={selectedOption} onChange={handleMenuOptionChange}>
        <Combobox.Options as="div" static hold className="flex divide-x divide-gray-100">
          {panesToShow[0] && (
            <div id="blue-transactions-menu" className={classNames('max-h-[32rem] min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4')}>
              <h2 className="mt-2 mb-4 text-base font-medium text-gray-500">{t('markThisTransaction')}</h2>
              <div className="-mx-2 text-sm text-gray-700">
                {options
                  .filter((option) => {
                    if (!firstTransaction) return true;
                    return firstTransaction.quantity < 0 ? option.requiresNegativeAmount : option.requiresPositiveAmount;
                  })
                  .map((item) => (
                    <Combobox.Option
                      as="div"
                      key={item.id}
                      value={item}
                      data-testid={item.id}
                      className={({ active }) => classNames(
                        'flex cursor-default select-none items-center rounded-md p-2',
                        item.id === selectedOption?.id ? 'bg-brandBlue-500 hover:bg-brandBlue-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-900',
                      )}
                    >
                      {({ active }) => (
                        <>
                          <item.Icon className="h-6 w-6 flex-none rounded-full" />
                          <span className="ml-3 flex-auto truncate">{item.name}</span>
                          {active && item.nextLevel && (
                            <>
                              <ChevronRightIcon className="hidden sm:inline ml-3 h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                              <ChevronDownIcon className="sm:hidden ml-3 h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
                            </>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
              </div>
              {!!selectedOption && !selectedOption?.nextLevel ? (
                <div id="blue-transactions-signle-level-buttons" className="grid grid-cols-2 gap-2 mt-4">
                  <div className="col-start-2 grid grid-cols-2 gap-2">
                    <Button text={tB('cancel')} onClick={() => handleCancel(true)} size="lg" />
                    <Button text={tB('save')} onClick={handleSaveLabelOnly} size="lg" withAccent enabled={!loading} spinnerOn={loading} />
                  </div>
                </div>
              ) : (
                isMobileLayout(currentBreakpoint) && (
                  <div id="blue-transactions-single-level-buttons" className="w-full flex justify-center mt-4">
                    <Button text={tB('cancel')} onClick={() => handleCancel(true)} size="lg" />
                  </div>
                )
              )}
            </div>
          )}
          {panesToShow[1] && firstTransaction && selectedOption && (
            <div id="blue-transactions-submenu" className="h-[32rem] sm:w-1/2 flex-none px-6 py-4 flex-col divide-y divide-gray-100 overflow-y-auto flex">
              <h2 className="mt-2 mb-4 text-base font-medium text-gray-500">{t('whichItem')}</h2>
              <div className="flex flex-auto flex-col justify-between overflow-hidden">
                <Sublist
                  selectedOption={selectedOption}
                  accountId={accountId}
                  linkedAttribute={linkedAttribute}
                  loanAccountsForRepaymentTagging={loanAccountsForRepaymentTagging}
                  // if it is for save, always close the dialog (regardless of what handleCancel will do)
                  handleCloseAfterSave={() => handleCancel(true)}
                  // if this is a mobile layout, cancel in Sublist must not close the dialog (only go back to the main menu)
                  handleCancel={isMobileLayout(currentBreakpoint) ? () => handleCancel(false) : () => handleCancel(true)}
                  transactions={transactions}
                />
              </div>
            </div>
          )}
        </Combobox.Options>
      </Combobox>
    </div>
  );
}
BlueTransactionsDialog.propTypes = {
  transactionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  category: PropTypes.string,
};
BlueTransactionsDialog.defaultProps = {
  category: 'deposits',
};
